import Big from 'big.js'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useRef, useState } from 'react'

import IconContainer from './icons/IconContainer'

export const SLIPPAGE_OPTIONS = [0.25, 0.5, 1]
export const CC_SLIPPAGE_OPTIONS = [1.5, 2, 2.5]
export const SLIPPAGE_DEFAULT = 0.5
export const CC_SLIPPAGE_DEFAULT = 2
export const SLIPPAGE_MAX = 100

function Slippage({
  defaultValue = SLIPPAGE_DEFAULT,
  disabled = false,
  onChange = null,
  options = SLIPPAGE_OPTIONS,
  value = SLIPPAGE_DEFAULT
}) {
  const { t } = useTranslation()
  const inputRef = useRef(null)
  const [showSlippageOptions, setShowSlippageOptions] = useState(false)
  const [showCustomInput, setShowCustomInput] = useState(false)
  const slippageWarning = value > options.slice(-1)
  const handleSlippageToleranceClick = function (_slippageTolerance) {
    onChange(_slippageTolerance)
    setShowCustomInput(false)
  }

  const handleCustomClick = function () {
    setShowCustomInput(true)
    onChange(null)
    inputRef.current?.focus()
  }

  const handleInputChange = function (_value) {
    const _slippageTolerance = _value.startsWith('.') ? `0${_value}` : _value
    if (
      (_slippageTolerance.match(/^[0-9]{1,2}(?:\.[0-9]{0,3})?$/) ||
        _slippageTolerance === '') &&
      Big(_slippageTolerance || 0).lte(SLIPPAGE_MAX)
    ) {
      onChange(_slippageTolerance)
    }
  }

  useEffect(
    function () {
      if (showCustomInput) {
        inputRef.current.focus()
      }
    },
    [showCustomInput]
  )

  return (
    <>
      <div className="flex justify-between">
        <span className="font-medium">{t('slippage-tolerance')}</span>
        <button
          className={`flex items-center ${
            disabled ? 'cursor-not-allowed' : ''
          }`}
          disabled={disabled}
          onClick={() => setShowSlippageOptions(!showSlippageOptions)}
          type="button"
        >
          <span
            className={`font-medium ${
              slippageWarning ? 'text-red-600' : 'text-gray-500'
            }`}
          >
            {value}%
          </span>
          <IconContainer
            className={`ml-2 h-2 w-2 ${
              showSlippageOptions ? 'rotate-180 transform' : ''
            }`}
            name="caret"
          />
        </button>
      </div>
      <div
        className={`mt-6 space-x-4 text-xs ${
          showSlippageOptions ? 'flex' : 'hidden'
        }`}
      >
        <button
          className="bg-teal-primary flex h-8 items-center rounded px-3 py-2 font-bold"
          data-sentry="slippage-default"
          onClick={() => handleSlippageToleranceClick(defaultValue)}
          type="button"
        >
          {t('default')}
        </button>
        <div className="bg-blue-5 grid h-8 w-full grid-cols-4 items-center space-x-1 rounded p-0.5 text-center">
          {options.map(option => (
            <button
              className={`
                font-bold
                ${value === option ? 'bg-blue-secondary h-full rounded' : ''}`}
              data-sentry="slippage-preset"
              key={option}
              onClick={() => handleSlippageToleranceClick(option)}
              type="button"
            >
              {option}%
            </button>
          ))}
          {!showCustomInput && (
            <button
              className="font-bold"
              data-sentry="slippage-custom"
              onClick={() => handleCustomClick()}
            >
              {t('custom')}
            </button>
          )}
          {showCustomInput && (
            <div className="flex items-center">
              <input
                className="bg-blue-5 placeholder:text-gray-5 w-8 text-right outline-none"
                data-sentry="slippage-custom-input"
                id="slippage-tolerance"
                inputMode="numeric"
                onBlur={() => !value && onChange(defaultValue)}
                onChange={e => handleInputChange(e.target.value)}
                placeholder="0"
                ref={inputRef}
                type="text"
                value={value || ''}
              />
              <label
                className={!value ? 'text-gray-5' : ''}
                htmlFor="slippage-tolerance"
              >
                %
              </label>
            </div>
          )}
        </div>
      </div>
      {slippageWarning && (
        <div className="bg-red-02 mt-8 rounded px-4 py-2">
          <p className="text-sm">{t('slippage-warning')}</p>
        </div>
      )}
    </>
  )
}

export default Slippage
